import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Users,
  Wallet,
  CircleDot,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchAccountsListData,
  getAccounts,
  getTotalAccounts,
  getAccountsFilter,
  setAccountsFilter,
  getAccountsLoading,
  setEditAccount,
  toggleAccountModal,
  fetchAccountTypesList,
  fetchPartiesList,
  getParties,
  getAccountTypes,
  getTotalCredit,
  getTotalDebit,
} from "../../features/accounts/accountsSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AccountsProps, AccountsFilterType, IFormInput, OptionsProps } from "../../types/accounts";
import AccountFormModal from "./AccountFormModal";

const sortByOptions = [
  { value: "id", label: t.accounts.sortOptions.id },
  { value: "amount", label: t.accounts.sortOptions.amount },
  { value: "status", label: t.accounts.sortOptions.status },
  { value: "created_at", label: t.accounts.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.accounts.sortOrderOptions.asc },
  { value: "desc", label: t.accounts.sortOrderOptions.desc },
];

const statusFilterOptions = [
  { value: 1, label: t.accounts.statusOptions.income },
  { value: 2, label: t.accounts.statusOptions.expense },
];

const AccountList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const accounts = useAppSelector(getAccounts);
  const totalAccounts = useAppSelector(getTotalAccounts);
  const filter = useAppSelector(getAccountsFilter);
  const loading = useAppSelector(getAccountsLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const parties = useAppSelector(getParties);
  const accountTypes = useAppSelector(getAccountTypes);
  const totalCredit = useAppSelector(getTotalCredit);
  const totalDebit = useAppSelector(getTotalDebit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const [partyOptions, setPartyOptions] = useState<OptionsProps[]>([]);
  const [accountTypeOptions, setAccountTypeOptions] = useState<OptionsProps[]>([]);

  const { register, control, handleSubmit, reset, setValue, getValues } =
    useForm<AccountsFilterType>({
      defaultValues: { ...filter },
    });

  const fetchData = () => {
    dispatch(
      fetchAccountsListData({
        page,
        size: pageSize,
        search: filter.search,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        party_id: filter.party_id,
        account_type_id: filter.account_type_id,
        status: filter.status,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, filter]);

  useEffect(() => {
    if (selectedBranch) {
      setPage(1);
      fetchData();
    }
  }, [selectedBranch]);

  useEffect(() => {
    dispatch(fetchAccountTypesList());
    dispatch(fetchPartiesList());
  }, []);

  useEffect(() => {
    const options =
      parties?.map((party: any) => ({ value: party.id, label: party.firm_name })) ?? [];
    setPartyOptions(options);
  }, [parties]);

  useEffect(() => {
    const options =
      accountTypes?.map((at: any) => ({ value: at.id, label: at.name })) ?? [];
    setAccountTypeOptions(options);
  }, [accountTypes]);

  const onApplyFilters = (data: AccountsFilterType) => {
    dispatch(
      setAccountsFilter({
        search: data.search || null,
        party_id: data.party_id || null,
        account_type_id: data.account_type_id || null,
        status: data.status || null,
        fromDate: data.fromDate || null,
        toDate: data.toDate || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults: AccountsFilterType = {
      search: null,
      party_id: null,
      account_type_id: null,
      status: null,
      fromDate: null,
      toDate: null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setAccountsFilter(defaults));
    setPage(1);
  };

  const handleShowForm = async (type: number) => {
    await dispatch(setEditAccount(null));
    dispatch(toggleAccountModal(type));
  };

  const handleSaveAccount = async (data: IFormInput) => {
    try {
      const storeEdit = (await import("../../app/store")).store.getState()
        .accounts.editAccount;

      if (storeEdit && storeEdit.id) {
        const response: any = await PUT(`/v1/accounts/${storeEdit.id}`, {
          amount: data.amount,
          account_type_id: data.account_type_id,
          status: data.status,
          description: data.description,
        });
        if (response.data.status === "success") {
          await dispatch(setEditAccount(null));
          if (page === 1) fetchData();
          else setPage(1);
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } else {
        const response: any = await POST("/v1/accounts", {
          amount: data.amount,
          account_type_id: data.account_type_id,
          status: data.status,
          description: data.description,
        });
        if (response.data.status === "success") {
          if (page === 1) fetchData();
          else setPage(1);
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const handleDeleteAccount = async (id: number) => {
    const response = await confirmDelete(
      t.accounts.deleteConfirmText,
      t.accounts.deleteConfirmTitle,
      t.accounts.deleteConfirmButton,
      true
    );

    if (response.confirmed) {
      setActionLoadingId(id);
      try {
        const res: any = await DELETE(`/v1/accounts/${id}`, {
          password: response.password,
        });
        if (res.data.status === "success") {
          toast.success(res.data.message, { ...(toastStyles as any) });
          const newPage =
            accounts.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) fetchData();
          else setPage(newPage);
        } else {
          toast.error(res.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            error?.data?.message ||
            t.common.somethingWentWrong,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const handleEditAccount = async (account: AccountsProps) => {
    await dispatch(setEditAccount(account));
    dispatch(toggleAccountModal(account?.status === 0 ? 1 : 2));
  };

  const handleExportReport = async () => {
    const values = getValues();
    const fromDate = values.fromDate || filter.fromDate;
    const toDate = values.toDate || filter.toDate;

    if (!fromDate || !toDate) {
      toast.error((t.accounts as any).generateReportDateRequired, { ...(toastStyles as any) });
      return;
    }

    try {
      const res: any = await GET(`/v1/get-accounts-report`, {
        fromDate,
        toDate,
        search: values.search || filter.search || null,
        party_id: values.party_id || filter.party_id || null,
        account_type_id: values.account_type_id || filter.account_type_id || null,
        status: values.status ?? filter.status ?? null,
      });
      if (res.data.status === "success") {
        toast.success(res.data.message, { ...(toastStyles as any) });
        const downloadLink = res.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (!newWindow || newWindow.closed || typeof newWindow.closed == "undefined") {
            throw new Error("Pop-up blocked");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(res.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const totalPages = Math.ceil(totalAccounts / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalAccounts === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalAccounts);
  const balance = totalCredit - totalDebit;

  const renderAccountInfo = (account: AccountsProps) => {
    if (account.account_type_id && !account.payment_id) {
      return (
        <span>
          {account?.account_type?.name || "-"}
          {account?.account_type?.phone ? ` - ${account.account_type.phone}` : ""}
        </span>
      );
    } else if (account?.payment_id) {
      return (
        <span>
          Payment ID: {account.payment_id}
          <br />
          Party: {account?.payment?.party?.firm_name || ""}
          {account?.payment?.party?.phone ? ` - ${account.payment.party.phone}` : ""}
        </span>
      );
    }
    return "-";
  };

  const isManualAccount = (account: AccountsProps) => !account.payment_id;

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="flex flex-wrap gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.accounts.filterToggle}
          </Button>

          {canAccess("accounts.create") && (
            <>
              <Button
                className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                onClick={() => handleShowForm(1)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t.accounts.addIncome}
              </Button>
              <Button
                className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                onClick={() => handleShowForm(2)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t.accounts.addExpense}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary cards: 3 plain white cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
        <Card className="p-4">
          <p className="text-sm text-gray-500">{t.accounts.totalIncome}</p>
          <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
            ₹{totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">{t.accounts.totalExpenses}</p>
          <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
            ₹{totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">{t.accounts.balance}</p>
          <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
            ₹{balance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">{t.accounts.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A]" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={t.accounts.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.fromDateLabel}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("fromDate")}
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.toDateLabel}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("toDate")}
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.partyLabel}
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="party_id"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          isClearable
                          styles={filterSelectStyles}
                          options={partyOptions}
                          placeholder={t.accounts.partyPlaceholder}
                          value={partyOptions.find((o) => o.value === field.value) || null}
                          onChange={(val: any) => field.onChange(val?.value || null)}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.accountTypeLabel}
                  </label>
                  <div className="relative">
                    <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="account_type_id"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          isClearable
                          styles={filterSelectStyles}
                          options={accountTypeOptions}
                          placeholder={t.accounts.accountTypePlaceholder}
                          value={accountTypeOptions.find((o) => o.value === field.value) || null}
                          onChange={(val: any) => field.onChange(val?.value || null)}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.statusLabel}
                  </label>
                  <div className="relative">
                    <CircleDot className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          isClearable
                          styles={filterSelectStyles}
                          options={statusFilterOptions}
                          placeholder={t.accounts.statusPlaceholder}
                          value={statusFilterOptions.find((o) => o.value === field.value) || null}
                          onChange={(val: any) => field.onChange(val?.value || null)}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.accounts.sortByPlaceholder}
                          value={sortByOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "id")}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.accounts.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.accounts.sortOrderPlaceholder}
                          value={sortOrderOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "desc")}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                  onClick={handleClearFilters}
                >
                  {t.accounts.clearFilters}
                </Button>
                <Button
                  type="button"
                  className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                  onClick={handleExportReport}
                >
                  {t.accounts.generateReport}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                >
                  {t.accounts.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && accounts.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.accounts.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && accounts.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.accounts.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div
            className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}
          >
            {accounts.map((account: AccountsProps, index: number) => (
              <Card key={account.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">
                    #{(page - 1) * pageSize + index + 1}
                  </span>
                  <Badge variant="secondary" className={account.status === 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                    {account.status === 0 ? t.accounts.income : t.accounts.expense}
                  </Badge>
                </div>
                <div className="mb-3 font-medium text-gray-900 text-sm">
                  {renderAccountInfo(account)}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm border-t pt-3">
                  <div>
                    <div className="text-xs text-gray-500">{t.accounts.table.amount}</div>
                    <div className="font-semibold text-[#113F67]">
                      {account?.amount?.toFixed(2) || "0.00"}
                    </div>
                  </div>
                  {account?.description && (
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{t.accounts.table.note}</div>
                      <div className="text-gray-700 truncate">{account.description}</div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <span className="text-xs text-gray-400">
                    {moment(account.created_at).format("DD-MM-YYYY hh:mm A")}
                  </span>
                  <div className="flex items-center gap-1">
                    {actionLoadingId === account.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                    ) : isManualAccount(account) ? (
                      <>
                        {canAccess("accounts.update") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10" onClick={() => handleEditAccount(account)}>
                            <Pencil className="h-4 w-4 text-[#113F67]" />
                          </Button>
                        )}
                        {canAccess("accounts.delete") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-red-50" onClick={() => handleDeleteAccount(account.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-16 font-semibold text-brand-dark">{t.accounts.table.srNo}</TableHead>
                  <TableHead className="font-semibold text-brand-dark">{t.accounts.table.accountTypeParty}</TableHead>
                  <TableHead className="font-semibold text-brand-dark">{t.accounts.table.status}</TableHead>
                  <TableHead className="font-semibold text-brand-dark text-right">{t.accounts.table.amount}</TableHead>
                  <TableHead className="font-semibold text-brand-dark">{t.accounts.table.note}</TableHead>
                  <TableHead className="font-semibold text-brand-dark">{t.accounts.table.date}</TableHead>
                  <TableHead className="font-semibold text-brand-dark text-right">{t.accounts.table.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.accounts.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {t.accounts.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account: AccountsProps, index: number) => (
                    <TableRow key={account.id} className="hover:bg-rose-50/40">
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{renderAccountInfo(account)}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={account.status === 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          {account.status === 0 ? t.accounts.income : t.accounts.expense}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {account?.amount?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-[200px] truncate text-sm">
                        {account?.description || "-"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(account.created_at).format("DD MMMM, YYYY hh:mm A")}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === account.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : isManualAccount(account) ? (
                          <div className="flex items-center justify-end gap-1">
                            {canAccess("accounts.update") && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditAccount(account)}>
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("accounts.delete") && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteAccount(account.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && accounts.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalAccounts} {t.accounts.pageOfLabel}
            </span>
            <span>{t.accounts.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(1)} disabled={page === 1}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(totalPages)} disabled={page === totalPages}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      <AccountFormModal handleSaveAccount={handleSaveAccount} />
    </div>
  );
};

export default AccountList;
